const normalizeName = (value = '') =>
  String(value || '')
    .toLocaleLowerCase('tr-TR')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const toNumberOrNull = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
};

const normalizePercent = (value) => {
  const n = toNumberOrNull(value);
  if (n === null) return null;
  if (n > 0 && n <= 1) return Math.round(n * 100);
  return Math.round(n);
};

const getIngredientRows = (recipe = {}) => {
  if (Array.isArray(recipe?.scaledIngredients) && recipe.scaledIngredients.length > 0) return recipe.scaledIngredients;
  if (Array.isArray(recipe?.ingredients) && recipe.ingredients.length > 0) return recipe.ingredients;
  if (Array.isArray(recipe?.malzemeler) && recipe.malzemeler.length > 0) return recipe.malzemeler;
  return [];
};

const isStapleIngredient = (ingredient = {}) => {
  const ingredientType = String(ingredient?.ingredientType || '').toLocaleLowerCase('tr-TR');
  const role = String(ingredient?.role || '').toLocaleLowerCase('tr-TR');
  const weightType = String(ingredient?.weightType || ingredient?.importance || ingredient?.priority || '').toLocaleLowerCase('tr-TR');
  const dictStaple = Boolean(ingredient?.dictionary?.isStaple || ingredient?.dictionary?.is_staple_food || ingredient?.dictionary?.is_staple);
  return Boolean(
    ingredient?.isStaple ||
    dictStaple ||
    ingredientType.includes('staple') ||
    role.includes('staple') ||
    weightType.includes('staple')
  );
};

const getMissingList = (recipe = {}) => {
  const candidates = [
    recipe?.match?.missingIngredients,
    recipe?.facts?.missingIngredients,
    recipe?.availability?.missingIngredients,
    recipe?.missingIngredients
  ];
  const seen = new Set();
  const result = [];

  candidates
    .filter(Array.isArray)
    .flat()
    .forEach((entry) => {
      const key = resolveMissingName(entry);
      if (!key || seen.has(key)) return;
      seen.add(key);
      result.push(entry);
    });

  return result;
};

const resolveMissingName = (entry) => {
  if (typeof entry === 'string') return normalizeName(entry);
  return normalizeName(
    entry?.name ||
    entry?.ingredient_name ||
    entry?.ingredientName ||
    entry?.malzeme ||
    entry?.urun_adi ||
    ''
  );
};

export const resolveMatchDisplay = (recipe = {}) => {
  const ingredientRows = getIngredientRows(recipe);
  const hasIngredientSignal = ingredientRows.length > 0;
  const normalizedPercent = normalizePercent(
    recipe?.match?.matchPercentage ??
    recipe?.facts?.matchPercentage ??
    recipe?.matchPercentage
  );

  const ingredientByName = new Map();
  ingredientRows.forEach((ing) => {
    const key = normalizeName(
      ing?.name ||
      ing?.ingredient_name ||
      ing?.ingredientName ||
      ing?.malzeme ||
      ing?.urun_adi ||
      ''
    );
    if (key) ingredientByName.set(key, ing);
  });

  const missingList = getMissingList(recipe);
  const missingNonStaple = missingList.filter((entry) => {
    if (entry && typeof entry === 'object' && isStapleIngredient(entry)) return false;
    const key = resolveMissingName(entry);
    if (!key) return true;
    const ingredient = ingredientByName.get(key);
    if (!ingredient) return true;
    return !isStapleIngredient(ingredient);
  });

  const explicitMissingCount = toNumberOrNull(
    recipe?.match?.missingCount ??
    recipe?.facts?.missingCount ??
    recipe?.missingCount
  );
  const missingCount = Number.isFinite(explicitMissingCount)
    ? Math.max(0, Math.round(explicitMissingCount), missingNonStaple.length)
    : missingNonStaple.length;

  const nonStapleRows = ingredientRows.filter((ing) => !isStapleIngredient(ing));
  const availabilityReady = nonStapleRows.some((ing) => typeof ing?.isAvailable === 'boolean' || typeof ing?.available === 'boolean');
  let computedPercent = null;
  if (availabilityReady && nonStapleRows.length > 0) {
    const matched = nonStapleRows.filter((ing) => (
      ing?.isAvailable === true ||
      ing?.available === true ||
      ing?.inInventory === true ||
      ing?.hasStock === true
    )).length;
    computedPercent = Math.round((matched / nonStapleRows.length) * 100);
  }

  let finalPercent = normalizedPercent;
  if (computedPercent !== null) finalPercent = computedPercent;
  if (hasIngredientSignal && missingCount === 0) finalPercent = 100;
  if (missingCount > 0 && finalPercent >= 100) finalPercent = Math.max(0, 100 - (missingCount * 10));
  if (finalPercent === null) finalPercent = missingCount === 0 ? 100 : 0;

  finalPercent = Math.max(0, Math.min(100, Math.round(finalPercent)));
  const isFull = missingCount === 0 && finalPercent >= 100;

  return {
    matchPercentage: finalPercent,
    missingCount,
    isFull
  };
};
